/**
 * Material Symbols are ligature icons: until the font is usable the browser prints the raw
 * icon name (e.g. "fire_extinguisher") as plain text. CSS in App.sass hides every
 * `.material-symbols-outlined` glyph (transparent text, fixed 1em box) unless the root element
 * carries `ICON_FONT_READY_CLASS`. This module sets that class once the font has really loaded.
 *
 * Fail-safe direction: if the API is missing, the font host is blocked or offline, the class is
 * never set and the icons stay invisible (never raw text).
 */
export const ICON_FONT_READY_CLASS = "icon-font-ready";
export const ICON_FONT_FACE = "Material Symbols Outlined";
const FONT_SPEC = `1em "${ICON_FONT_FACE}"`;

/** True if the icon font has been marked as ready on the given root element. */
export function isIconFontReady(root: HTMLElement = document.documentElement): boolean {
  return root.classList.contains(ICON_FONT_READY_CLASS);
}

/** True if the font set already holds a loaded face of the icon family (quoted names handled). */
function hasLoadedIconFace(fonts: FontFaceSet): boolean {
  try {
    if (typeof (fonts as any)[Symbol.iterator] !== "function") {
      return false;
    }
    return Array.from(fonts as Iterable<FontFace>).some(
      (face) =>
        face.status === "loaded" &&
        String(face.family).replace(/["']/g, "").trim() === ICON_FONT_FACE
    );
  } catch {
    return false;
  }
}

/**
 * Marks the root element once the icon font is loaded. Returns a disposer.
 * `document.fonts.load()` resolves with an empty list when no @font-face with that family is
 * registered (yet) and `check()` is then vacuously true, so readiness requires at least one
 * matching face whose status is "loaded"; otherwise we retry on `loadingdone`.
 */
export function watchIconFont(doc: Document = document): () => void {
  const fonts = (doc as any).fonts as FontFaceSet | undefined;
  if (!fonts || typeof fonts.load !== "function") {
    return () => undefined;
  }
  const root = doc.documentElement;
  let disposed = false;

  const markReady = () => {
    root.classList.add(ICON_FONT_READY_CLASS);
    fonts.removeEventListener?.("loadingdone", attempt);
  };

  const attempt = async () => {
    if (disposed || root.classList.contains(ICON_FONT_READY_CLASS)) {
      return;
    }
    try {
      const faces = await fonts.load(FONT_SPEC);
      if (!disposed && faces.length > 0 && faces.every((face) => face.status === "loaded")) {
        markReady();
      }
    } catch {
      // Font host blocked/offline: stay hidden; a later `loadingdone` may still succeed.
    }
  };

  fonts.addEventListener?.("loadingdone", attempt);
  // Warm cache: `fonts.load()` always resolves asynchronously, so avoid a blank frame when the
  // face is already loaded.
  if (hasLoadedIconFace(fonts)) {
    markReady();
  }
  void attempt();
  void fonts.ready?.then(attempt, () => undefined);

  return () => {
    disposed = true;
    fonts.removeEventListener?.("loadingdone", attempt);
  };
}

/**
 * Loads the icon font for canvas text and reports whether a real face is usable.
 * `fonts.check()` is vacuously true when no @font-face with that family is registered (e.g. the
 * stylesheet is blocked), which would draw the raw ligature name; so require a loaded face from
 * the `load()` result instead.
 */
export async function loadIconFontForCanvas(
  doc: Document = document,
  spec = `24px "${ICON_FONT_FACE}"`
): Promise<boolean> {
  try {
    const fonts = (doc as any).fonts as FontFaceSet | undefined;
    if (!fonts || typeof fonts.load !== "function") {
      return false;
    }
    const faces = await fonts.load(spec);
    return faces.length > 0 && faces.every((face) => face.status === "loaded");
  } catch {
    return false;
  }
}
